/**
 * Long-press drag — the pointer state machine behind the Interests and GOTY
 * boards (INVENTORY §7; prototype `capPD` / `dragActivate` / `ghostTo` /
 * `dragMove` / `dragEnd` / `dragCleanup`, lines 1310-1372).
 *
 * SCOPE. This module owns *pointer mechanics only*:
 *   - the 330 ms long-press timer and its 9 px movement cancel,
 *     `navigator.vibrate(25)` on activation, the `touchmove` scroll block;
 *   - imperative ghost positioning (never through React state — the prototype
 *     used `ghostRef` for exactly this reason, INVENTORY §2 row 9);
 *   - hit-testing the row under the pointer and computing the drop index;
 *   - edge auto-scroll (row ±14 px within 44 px, window ±12 px within
 *     130 px / 110 px);
 *   - the 450 ms post-drop tap suppression.
 *
 * It owns NO data mutation. The drop commits through the store
 * (`dragEnd()` → `placeTierAt` / `setIntAt`), which the caller supplies as
 * `onDrop`. Nothing here imports the store, so the geometry stays pure and
 * testable (`drag.test.ts` exercises it with plain numbers, no DOM).
 */

import { useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type { GameId } from './types.ts';

// ---------------------------------------------------------------------------
// constants — every one of these is a prototype literal (INVENTORY §7)
// ---------------------------------------------------------------------------

/** `this._dpT = setTimeout(() => this.dragActivate(), 330)` */
export const LONG_PRESS_MS = 330;
/** cancel the long press once the pointer moves more than this on either axis */
export const MOVE_CANCEL_PX = 9;
/** `this._dragged` guard — a tap this soon after a drop must not open a sheet */
export const TAP_SUPPRESS_MS = 450;
/** row auto-scroll trigger band, measured from either row edge */
export const ROW_EDGE_PX = 44;
/** row auto-scroll step per pointermove */
export const ROW_SCROLL_PX = 14;
/** window auto-scroll band at the top of the viewport */
export const WIN_TOP_PX = 130;
/** window auto-scroll band at the bottom of the viewport */
export const WIN_BOTTOM_PX = 110;
/** window auto-scroll step per pointermove */
export const WIN_SCROLL_PX = 12;
/** ghost is drawn with the pointer at this offset inside it */
export const GHOST_OFFSET_X = 34;
export const GHOST_OFFSET_Y = 108;
/** `navigator.vibrate(25)` on activation */
export const VIBRATE_MS = 25;

export type DragZone = 'int' | 'goty';

/** Structurally identical to the store's `DragOver`; kept local so lib/ never imports store/. */
export interface DropTarget {
  zone: DragZone;
  row: string;
  idx: number;
}

// ---------------------------------------------------------------------------
// pure geometry
// ---------------------------------------------------------------------------

/** The horizontal slice of a `DOMRect` the drop-index math actually reads. */
export interface CapRect {
  left: number;
  width: number;
}

/**
 * Insertion index for a pointer at `x` over a row whose remaining capsules
 * (the dragged one already removed) occupy `rects`, in DOM order.
 *
 * Prototype (line 1341): walk left to right, take the first capsule whose
 * horizontal midpoint is right of the pointer; fall through to the end of the
 * row when there is none. The dragged capsule is excluded by the caller, so
 * the index is already expressed in post-removal coordinates — which is what
 * `intOrderAfterDrop` / `tierOrderAfterDrop` splice into.
 */
export function dropIndex(rects: readonly CapRect[], x: number): number {
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (r && x < r.left + r.width / 2) return i;
  }
  return rects.length;
}

/** Long-press cancel test — `>9px` on either axis (strict, as in the prototype). */
export function exceedsSlop(dx: number, dy: number): boolean {
  return Math.abs(dx) > MOVE_CANCEL_PX || Math.abs(dy) > MOVE_CANCEL_PX;
}

/**
 * Row `scrollLeft` delta for a pointer at `x`. The right edge is tested first,
 * matching the prototype's `if … else if` — a row narrower than 88 px therefore
 * scrolls right, not left.
 */
export function rowScrollDelta(x: number, rect: { left: number; right: number }): number {
  if (x > rect.right - ROW_EDGE_PX) return ROW_SCROLL_PX;
  if (x < rect.left + ROW_EDGE_PX) return -ROW_SCROLL_PX;
  return 0;
}

/** Window scroll delta for a pointer at `y` (prototype line 1347). */
export function windowScrollDelta(y: number, innerHeight: number): number {
  if (y < WIN_TOP_PX) return -WIN_SCROLL_PX;
  if (y > innerHeight - WIN_BOTTOM_PX) return WIN_SCROLL_PX;
  return 0;
}

/** The prototype compared drop targets with `JSON.stringify`; this is that, typed. */
export function sameTarget(a: DropTarget | null, b: DropTarget | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.zone === b.zone && a.row === b.row && a.idx === b.idx;
}

/** `ghostTo` (line 1327) — pointer-centred, tilted 3°. */
export function ghostTransform(x: number, y: number): string {
  return 'translate(' + (x - GHOST_OFFSET_X) + 'px,' + (y - GHOST_OFFSET_Y) + 'px) rotate(-3deg)';
}

// ---------------------------------------------------------------------------
// DOM hit-testing
// ---------------------------------------------------------------------------

/**
 * The board row under the pointer, or null when the pointer is off-board or
 * over a row belonging to the other zone (prototype line 1337).
 */
export function rowUnder(x: number, y: number, zone: DragZone, doc: Document = document): HTMLElement | null {
  const el = doc.elementFromPoint(x, y);
  const row = el && typeof el.closest === 'function' ? el.closest('[data-gt-row]') : null;
  if (!row || row.getAttribute('data-gt-zone') !== zone) return null;
  return row as HTMLElement;
}

/** Live rects of a row's capsules, minus the one being dragged. */
export function capRectsIn(rowEl: Element, draggedId: GameId): CapRect[] {
  const dragged = String(draggedId);
  const out: CapRect[] = [];
  for (const c of Array.from(rowEl.querySelectorAll('[data-gt-cap]'))) {
    if (c.getAttribute('data-gt-cap') === dragged) continue;
    const r = c.getBoundingClientRect();
    out.push({ left: r.left, width: r.width });
  }
  return out;
}

// ---------------------------------------------------------------------------
// ghost registry — module scope so the ghost (mounted by App) and the gesture
// (owned by a screen) can meet without routing coordinates through React
// ---------------------------------------------------------------------------

let ghostEl: HTMLElement | null = null;
let lastX = 0;
let lastY = 0;

/**
 * `sheets/DragGhost.tsx` calls this from its ref callback. Registering applies
 * the current pointer position immediately, so the ghost is already in place on
 * its first paint (the prototype did this in the `setState` callback).
 */
export function registerGhost(el: HTMLElement | null): void {
  ghostEl = el;
  if (el) el.style.transform = ghostTransform(lastX, lastY);
}

function moveGhost(x: number, y: number): void {
  lastX = x;
  lastY = y;
  if (ghostEl) ghostEl.style.transform = ghostTransform(x, y);
}

// ---------------------------------------------------------------------------
// post-drop tap suppression
// ---------------------------------------------------------------------------

let lastDropAt = -Infinity;

/** Called on every gesture that reached the active state, drop or not. */
export function markDropped(now: number = Date.now()): void {
  lastDropAt = now;
}

/**
 * True while the synthetic click that follows a drop would still land
 * (prototype `this._dragged` + the 450 ms check at lines 1366 / 1801).
 */
export function tapSuppressed(now: number = Date.now()): boolean {
  return now - lastDropAt < TAP_SUPPRESS_MS;
}

/** Test seam — resets the module singletons. */
export function resetDragModule(): void {
  ghostEl = null;
  lastX = 0;
  lastY = 0;
  lastDropAt = -Infinity;
}

// ---------------------------------------------------------------------------
// the controller
// ---------------------------------------------------------------------------

export interface DragDeps {
  /** which board this screen is — a drop only registers inside its own zone */
  zone: DragZone;
  /** `true` while `store.drag` is set; lets cleanup skip a pointless store write */
  dragging: boolean;
  /** the store's current `dragOver`, read at pointerup to decide the commit */
  dragOver: DropTarget | null;
  /** long press fired — `store.dragStart` */
  onStart: (row: string, id: GameId) => void;
  /** hovered row/index changed — `store.setDragOver` */
  onOver: (ov: DropTarget | null) => void;
  /** dropped on a valid target — `store.dragEnd`, which commits AND clears */
  onDrop: () => void;
  /** aborted — `store.dragCancel` */
  onCancel: () => void;
}

export interface DragHandle {
  /** `capPD` — bind to a capsule's `onPointerDown` */
  capPD: (row: string, id: GameId) => (e: ReactPointerEvent) => void;
  /** guard a capsule's `onClick`: a tap within 450 ms of a drop is swallowed */
  suppressed: () => boolean;
}

interface Gesture {
  row: string;
  id: GameId;
  sx: number;
  sy: number;
  x: number;
  y: number;
  active: boolean;
}

/**
 * The prototype's gesture, as a hook. One instance per board screen; only one
 * board is ever mounted, and unmounting mid-drag cleans up.
 */
export function useDrag(deps: DragDeps): DragHandle {
  // Deps change identity every render; the listeners are installed once per
  // gesture, so they must read through a ref rather than close over a snapshot.
  const dref = useRef(deps);
  dref.current = deps;

  const gest = useRef<Gesture | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listeners = useRef<{
    mv: (e: PointerEvent) => void;
    up: () => void;
    cancel: () => void;
    ctx: (e: Event) => void;
  } | null>(null);
  const touchBlock = useRef<((e: TouchEvent) => void) | null>(null);

  const api = useRef<DragHandle | null>(null);
  const abort = useRef<() => void>(() => undefined);

  if (!api.current) {
    const cleanup = (wasActive: boolean, committed: boolean): void => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      const L = listeners.current;
      if (L) {
        window.removeEventListener('pointermove', L.mv);
        window.removeEventListener('pointerup', L.up);
        window.removeEventListener('pointercancel', L.cancel);
        window.removeEventListener('contextmenu', L.ctx);
        listeners.current = null;
      }
      if (touchBlock.current) {
        window.removeEventListener('touchmove', touchBlock.current);
        touchBlock.current = null;
      }
      gest.current = null;
      if (wasActive) markDropped();
      // `onDrop` already cleared the drag; only cancel when it did not run.
      if (!committed && (wasActive || dref.current.dragging)) dref.current.onCancel();
    };

    const activate = (): void => {
      const d = gest.current;
      if (!d) return;
      d.active = true;
      const block = (ev: TouchEvent): void => {
        const g = gest.current;
        if (g && g.active) ev.preventDefault();
      };
      touchBlock.current = block;
      window.addEventListener('touchmove', block, { passive: false });
      try {
        if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(VIBRATE_MS);
      } catch {
        /* vibrate is best-effort — blocked by permissions policy on some hosts */
      }
      moveGhost(d.x, d.y);
      dref.current.onStart(d.row, d.id);
    };

    const move = (ev: PointerEvent): void => {
      const d = gest.current;
      if (!d) return;
      d.x = ev.clientX;
      d.y = ev.clientY;
      if (!d.active) {
        if (exceedsSlop(d.x - d.sx, d.y - d.sy)) cleanup(false, false);
        return;
      }
      moveGhost(d.x, d.y);

      const zone = dref.current.zone;
      const rowEl = rowUnder(d.x, d.y, zone);
      let over: DropTarget | null = null;
      if (rowEl) {
        over = {
          zone,
          row: rowEl.getAttribute('data-gt-row') ?? '',
          idx: dropIndex(capRectsIn(rowEl, d.id), d.x),
        };
        rowEl.scrollLeft += rowScrollDelta(d.x, rowEl.getBoundingClientRect());
      }
      const wy = windowScrollDelta(d.y, window.innerHeight);
      if (wy) window.scrollBy(0, wy);

      if (!sameTarget(dref.current.dragOver, over)) dref.current.onOver(over);
    };

    const end = (): void => {
      const d = gest.current;
      const wasActive = !!(d && d.active);
      const over = dref.current.dragOver;
      const commit = wasActive && !!over;
      if (commit) dref.current.onDrop();
      cleanup(wasActive, commit);
    };

    api.current = {
      capPD: (row, id) => (e: ReactPointerEvent) => {
        if (dref.current.dragging || gest.current) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        gest.current = { row, id, sx: e.clientX, sy: e.clientY, x: e.clientX, y: e.clientY, active: false };
        const mv = (ev: PointerEvent): void => move(ev);
        const up = (): void => end();
        const cancel = (): void => cleanup(!!gest.current?.active, false);
        const ctx = (ev: Event): void => ev.preventDefault();
        listeners.current = { mv, up, cancel, ctx };
        window.addEventListener('pointermove', mv);
        window.addEventListener('pointerup', up);
        window.addEventListener('pointercancel', cancel);
        window.addEventListener('contextmenu', ctx);
        timer.current = setTimeout(activate, LONG_PRESS_MS);
      },
      suppressed: () => tapSuppressed(),
    };

    // Unmounting mid-gesture must still pull the window listeners.
    abort.current = () => cleanup(false, false);
  }

  useEffect(() => () => abort.current(), []);

  return api.current;
}
